import '.p{position:absolute;}.ssr-plugin'
import '.bi{inset:0px;}.ssr-plugin'
import '.hr{top:76px;}.ssr-plugin'
import '.hs{left:284px;}.ssr-plugin'
import '.a6{right:16px;}.ssr-plugin'
import '.ht{height:calc(100vh - 92px);}.ssr-plugin'
import '.bj{overflow:hidden;}.ssr-plugin'
import '.fh{z-index:3;}.ssr-plugin'
import '.hu{top:60px;}.ssr-plugin'
import '.c6{overflow-y:scroll;}.ssr-plugin'
import '.n{position:relative;}.ssr-plugin'
import '.a{display:flex;}.ssr-plugin'
import '.c{justify-content:center;}.ssr-plugin'
import '.ge{overflow-x:hidden;}.ssr-plugin'
import '.hv{top:14px;}.ssr-plugin'
import '.dp{left:0px;}.ssr-plugin'
import '.dr{right:0px;}.ssr-plugin'
import '.w{pointer-events:none;}.ssr-plugin'
import '.h{width:100%;}.ssr-plugin'
import '.hw{max-width:750px;}.ssr-plugin'
import '.hx{padding:16px 12px;}.ssr-plugin'
import '.k{margin:0 auto;}.ssr-plugin'
import '.hy{max-height:520px;}.ssr-plugin'
import '.hz{min-height:124px;}.ssr-plugin'
import '.dq{top:0px;}.ssr-plugin'
import { LexicalErrorBoundary } from '@lexical/react/LexicalErrorBoundary'
import { ContentEditable } from '@lexical/react/LexicalContentEditable'
import { RichTextPlugin } from '@lexical/react/LexicalRichTextPlugin'
import { HistoryPlugin } from '@lexical/react/LexicalHistoryPlugin'
import {
  InitialConfigType,
  LexicalComposer,
} from '@lexical/react/LexicalComposer'
import { ListPlugin } from '@lexical/react/LexicalListPlugin'
import { LinkPlugin } from '@lexical/react/LexicalLinkPlugin'
import { TablePlugin } from '@lexical/react/LexicalTablePlugin'
import { $generateNodesFromDOM } from '@lexical/html'
import { TableCellNode, TableNode, TableRowNode } from '@lexical/table'
import {
  $createParagraphNode,
  $getRoot,
  isHTMLElement,
  type LexicalEditor,
} from 'lexical'
import { useEffect, useId, useMemo, useRef, useState } from 'react'

import { FloatingLinkPlugin } from './plugins/FloatingLinkPlugin.js'
import { BehaviourPlugin } from './plugins/BehaviourPlugin.js'
import { ValuePlugin } from './plugins/ValuePlugin.js'
import { EmbedPlugin } from './plugins/EmbedPlugin.js'
import { ImagePlugin } from './plugins/ImagePlugin.js'

import { colors } from '../../tokens/colors.js'
import { radius } from '../../utils/radius.js'
import { Text } from '../Text/index.js'
import { Toolbar } from './Toolbar.js'
import { NODES } from './nodes.js'
import { Style, styled } from 'inlines'
import { LabelDescription } from '../LabelDescription/index.js'
import { EDITOR_STYLES } from './EditorStyle.js'
import { MiniNavigation } from './MiniNavigation.js'

type RichTextEditorProps = {
  value?: string
  onChange: (value: string) => void
  placeholder?: string
  disabled?: boolean
  error?: boolean
  style?: Style
  onFocus?: () => void
  onBlur?: () => void
  label?: string
  description?: string
  errorMessage?: string
}

const removeTableCss = (
  editor: LexicalEditor,
  target: TableNode | TableCellNode | TableRowNode,
) => {
  const node = target.exportDOM(editor)
  const { element } = node
  if (isHTMLElement(element)) element.removeAttribute('style')
  return node
}

const htmlExportMap = new Map()
htmlExportMap.set(TableNode, removeTableCss)
htmlExportMap.set(TableRowNode, removeTableCss)
htmlExportMap.set(TableCellNode, removeTableCss)

const BASE_CONFIG: Omit<InitialConfigType, 'namespace' | 'editorState'> = {
  onError: console.error,
  nodes: NODES,
  theme: {
    text: {
      italic: 'italic',
      strikethrough: 'strikethrough',
      underline: 'underline',
      underlineStrikethrough: 'underline-strikethrough',
    },
  },
  html: {
    export: htmlExportMap,
  },
}

let parser: DOMParser
const EMPTY_HTML_RE = /^\s*(<p><br><\/p>|<p><\/p>)\s*$/i
const normalizeHtml = (html: string) => (EMPTY_HTML_RE.test(html) ? '' : html)

const RichTextEditor = ({
  value,
  onChange,
  placeholder,
  disabled,
  style,
  onFocus,
  onBlur,
  label,
  description,
  error,
  errorMessage = 'This field is required',
}: RichTextEditorProps) => {
  const [fullScreen, setFullScreen] = useState(false)

  const id = useId()
  const ref = useRef<HTMLDivElement>(null)
  const scrollContainerRef = useRef<HTMLDivElement>(null)

  const isSmall = (ref.current?.clientWidth ?? 0) < 600

  // Guarda sempre o valor mais recente pra inicializar o editorState no remount
  const valueRef = useRef<string | undefined>(value)
  useEffect(() => {
    valueRef.current = value
  }, [value])

  // ✅ Remonta UMA vez quando value chega pela primeira vez (undefined -> string)
  const [boot, setBoot] = useState(0)
  const hadValueOnce = useRef(false)
  useEffect(() => {
    if (!hadValueOnce.current && value != null) {
      hadValueOnce.current = true
      setBoot((x) => x + 1)
    }
  }, [value])

  const inlineConfig = useMemo<InitialConfigType>(() => {
    return {
      ...BASE_CONFIG,
      namespace: `based_ui_rte_${id}_inline_${boot}`,
      editorState: (editor) => {
        const html = normalizeHtml(valueRef.current ?? '')
        const root = $getRoot()
        root.clear()

        if (html === '') {
          root.append($createParagraphNode())
          return
        }

        parser ??= new DOMParser()
        const dom = parser.parseFromString(html, 'text/html')
        const nodes = $generateNodesFromDOM(editor, dom)
        root.append(...nodes)
      },
    }
  }, [id, boot])

  const fullConfig = useMemo<InitialConfigType>(() => {
    return {
      ...BASE_CONFIG,
      namespace: `based_ui_rte_${id}_full_${boot}`,
      editorState: (editor) => {
        const html = normalizeHtml(valueRef.current ?? '')
        const root = $getRoot()
        root.clear()

        if (html === '') {
          root.append($createParagraphNode())
          return
        }

        parser ??= new DOMParser()
        const dom = parser.parseFromString(html, 'text/html')
        const nodes = $generateNodesFromDOM(editor, dom)
        root.append(...nodes)
      },
    }
  }, [id, boot])

  useEffect(() => {
    if (scrollContainerRef?.current && value) {
      const timeoutId = setTimeout(() => {
        scrollContainerRef.current?.scrollTo({
          top: 0,
          behavior: 'instant' as any,
        })
      }, 0)
      return () => clearTimeout(timeoutId)
    }
  }, [fullScreen])

  const composerKey = `${id}:${boot}:${fullScreen ? 'full' : 'inline'}`

  return fullScreen ? (
    <styled.div className="p bi hr hs a6 ht bj fh"
      style={{
        background: colors.neutralInverted100,
        border: `1px solid ${colors.neutral20Adjusted}`,
        borderRadius: radius[20],
        ...style,
      }}
    >
      <LexicalComposer key={composerKey} initialConfig={fullConfig}>
        <Toolbar
          fullScreen={fullScreen}
          setFullScreen={setFullScreen}
          isSmall={isSmall}
          disabled={disabled}
          style={{
            position: 'sticky',
            top: 0,
            left: 0,
            right: 0,
            zIndex: 4,
            background: colors.neutralInverted100,
          }}
        />
        <div className="p hu bi c6"
          ref={scrollContainerRef}
        >
          <styled.div className="n a c ge"
            style={{
              ...EDITOR_STYLES,
            }}
          >
            <RichTextPlugin
              contentEditable={
                <ContentEditable
                  onFocus={onFocus}
                  onBlur={onBlur}
                  className="editor"
                  style={{
                    outline: 'none',
                    padding: isSmall ? '20px 12px' : '32px 12px',
                    width: '100%',
                    maxWidth: 750,
                    margin: '0 auto',
                    pointerEvents: disabled ? 'none' : 'auto',
                  }}
                  tabIndex={disabled ? -1 : undefined}
                />
              }
              placeholder={
                <div className="p hv dp dr w"
                >
                  <div className="h hw hx k"
                  >
                    <Text color="neutral60" variant="display-regular">
                      {placeholder}
                    </Text>
                  </div>
                </div>
              }
              ErrorBoundary={LexicalErrorBoundary}
            />
          </styled.div>

          <BehaviourPlugin />
          <ValuePlugin value={value} onChange={onChange} disabled={disabled} />

          <ListPlugin />
          <LinkPlugin />
          <HistoryPlugin />
          <FloatingLinkPlugin />
          <TablePlugin />
          <EmbedPlugin />
          <ImagePlugin />
        </div>
      </LexicalComposer>

      <MiniNavigation value={value} scrollContainerRef={scrollContainerRef} />
    </styled.div>
  ) : (
    <LabelDescription
      label={label}
      description={description}
      error={error && errorMessage}
    >
      <styled.div className="h bj n"
        ref={ref}
        tabIndex={disabled ? -1 : undefined}
        style={{
          border: `1px solid ${disabled ? 'transparent' : colors.neutral20Adjusted}`,
          borderRadius: radius[12],
          pointerEvents: disabled ? 'none' : 'auto',
          background: disabled ? colors.neutral5 : 'inherit',
          color: disabled ? colors.neutral20 : 'inherit',
          ...style,
        }}
      >
        <LexicalComposer key={composerKey} initialConfig={inlineConfig}>
          <Toolbar
            fullScreen={fullScreen}
            setFullScreen={setFullScreen}
            isSmall={isSmall}
            disabled={disabled}
          />
          <div className="n bi c6 hy hz"
          >
            <styled.div className="n a c ge"
              style={{
                ...EDITOR_STYLES,
              }}
            >
              <RichTextPlugin
                contentEditable={
                  <ContentEditable
                    onFocus={onFocus}
                    onBlur={onBlur}
                    className="editor"
                    style={{
                      outline: 'none',
                      padding: isSmall ? '20px 12px' : '20px 24px',
                      width: '100%',
                      maxWidth: 750,
                      margin: '0 auto',
                      pointerEvents: disabled ? 'none' : 'auto',
                    }}
                    tabIndex={disabled ? -1 : undefined}
                  />
                }
                placeholder={
                  <div className="p dp dr dq w"
                  >
                    <div className="h hw k"
                      style={{
                        padding: isSmall ? '18px 12px' : '18px 24px',}}
                    >
                      <Text color="neutral60" variant="display-regular">
                        {placeholder}
                      </Text>
                    </div>
                  </div>
                }
                ErrorBoundary={LexicalErrorBoundary}
              />
            </styled.div>

            <BehaviourPlugin />
            <ValuePlugin
              value={value}
              onChange={onChange}
              disabled={disabled}
            />

            <ListPlugin />
            <LinkPlugin />
            <HistoryPlugin />
            <FloatingLinkPlugin />
            <TablePlugin />
            <EmbedPlugin />
            <ImagePlugin />
          </div>
        </LexicalComposer>
      </styled.div>
    </LabelDescription>
  )
}

const RichTextEditorStyles = EDITOR_STYLES

export { RichTextEditor, RichTextEditorStyles }
export type { RichTextEditorProps }
